import { Button } from "@msuite/ui/button";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { type CellContext, RowActionsWrapper } from "@msuite/ui/table";
import { Tooltip } from "@msuite/ui/tooltip";
import { responseHandler } from "@msuite/ui/utils";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { useCitadel } from "@/hooks/use-citadel";
import { useDecideIncomingInvoiceApproval } from "@/hooks/use-decide-incoming-invoice-approval";
import { ReviewApprovalButton } from "../review-approval-button";
import type { ApprovalRow } from "./approval-row";

/**
 * The caller's own row reviews the invoice on its turn, or takes its decision back while nobody
 * after it in the chain has decided. A pending handover is withdrawn by the one who made it or by
 * anyone who may edit the invoice.
 */
export function ApprovalRowActions({ cell }: CellContext<ApprovalRow, "actions">) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const { user } = useAuth();
	const { decide, isDeciding } = useDecideIncomingInvoiceApproval();
	const { citadel } = useCitadel();
	const qc = useQueryClient();

	/** Functions */
	async function handleOnTakeBack() {
		await decide(approval.incomingInvoiceId, approval.id, { state: "pending", comment: null });
	}

	async function handleOnWithdraw() {
		const response = await client.incoming_invoices[":incomingInvoiceId"].approvals[
			":approvalId"
		].$delete({
			param: { incomingInvoiceId: approval.incomingInvoiceId, approvalId: approval.id },
		});
		await responseHandler(response);
		await qc.invalidateQueries({
			predicate: invalidate(["incoming-invoices", "incoming-invoice-approvals-mine"]),
		});
	}

	/** Computed */
	const approval = cell.row.original;
	// Only the person an approval asks decides it, and nobody once the invoice is paid.
	const isOwn =
		approval.user.id === user?.id &&
		approval.isRequired &&
		!approval.isInvoicePaid &&
		!approval.isInvoiceDuplicate;
	const canReview = isOwn && approval.state === "pending";
	const canTakeBack = isOwn && approval.canTakeBack;
	const canWithdraw =
		approval.assignment === "assigned" &&
		approval.state === "pending" &&
		!approval.isInvoicePaid &&
		(approval.assignedBy === user?.id || citadel?.can("update", "IncomingInvoice") === true);

	/** Render */
	return (
		<RowActionsWrapper to={null}>
			{canReview && (
				<ReviewApprovalButton
					incomingInvoiceId={approval.incomingInvoiceId}
					approvalId={approval.id}
					turn={approval.turn}
					mayDecideAhead={approval.assignment === "default"}
					skipCount={approval.skipCount}
				/>
			)}
			{canTakeBack && (
				<Tooltip label={t("incoming_invoices.take_back_decision")}>
					<Button
						icon={<Icon name="Undo2" />}
						size="xxs"
						variant="ghost"
						isLoading={isDeciding}
						onClick={handleOnTakeBack}
						data-cy="take-back-incoming-invoice-decision-button"
					/>
				</Tooltip>
			)}
			{canWithdraw && (
				<Tooltip label={t("incoming_invoices.handover.withdraw")}>
					<Button
						icon={<Icon name="UserMinus" />}
						size="xxs"
						variant="danger-ghost"
						onClick={handleOnWithdraw}
						data-cy="withdraw-incoming-invoice-handover-button"
					/>
				</Tooltip>
			)}
		</RowActionsWrapper>
	);
}
